import { Field, Label } from '@headlessui/react';
import { FC, MouseEvent, TouchEvent, useId, useState, WheelEvent } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, CrossIcon, SearchIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { Checkbox } from '../../../../components/checkbox/index';
import { Input } from '../../../../components/input/index';
import Loader from '../../../../components/loader/index';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/popover/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { SafeHooks, useFormat, useModalState } from '../../../../hooks/index';
import { Safe } from '../../../../types/safes.types';
import { FormSchema } from '../convert-modal.validation';

type SelectedSafe = NonNullable<FormSchema['stepOne']['safes']>[number];

type FilterItemProps = {
  checked: boolean;
  currencySign?: string;
  id: string;
  onChange: (state: boolean) => void;
  safe: Safe;
};

export const FilterItem: FC<FilterItemProps> = ({ id, onChange, safe, checked, currencySign }) => {
  const { format } = useFormat();

  return (
    <Field>
      <div
        className={twMerge(
          'relative flex w-full cursor-pointer items-center justify-between gap-2 rounded-[4px] px-[6px] py-2 text-sm text-gray-700 hover:bg-gray-50',
          checked && 'bg-brand-25 hover:bg-brand-25',
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <Checkbox checked={checked} id={`select-safe-${id}`} onChange={onChange} />
          <span className="max-w-[200px] truncate">{safe.stakeholder.fullName}</span>
        </div>
        <span className="shrink-0">
          {currencySign}
          {Number(safe.investment).toLocaleString('en-US')} - {format(safe.issueDate, 'dd/MM/yyyy')}
        </span>
        {/* Makes the whole row toggle the checkbox, not just the 16px box. */}
        <Label
          className="absolute left-0 top-0 h-full w-full cursor-pointer"
          htmlFor={`select-safe-${id}`}
        />
      </div>
    </Field>
  );
};

export type SafeSelectProps = {
  companyId: string;
  control: Control<FormSchema>;
  currencySign?: string;
  setValue: UseFormSetValue<FormSchema>;
};
const take = 20;
const visibleChipCount = 2;

const stopScrollLockPropagation = (event: TouchEvent | WheelEvent) => event.stopPropagation();

const toSelectedSafe = (safe: Safe): SelectedSafe => ({
  id: safe.id,
  stakeholder: {
    fullName: safe.stakeholder.fullName,
    id: safe.stakeholder.id,
  },
  investment: Number(safe.investment),
  issueDate: new Date(safe.issueDate),
  floorValue: Number(safe.floorValue),
});

export const SafeSelect: FC<SafeSelectProps> = ({ companyId, setValue, currencySign, control }) => {
  const { format } = useFormat();
  const listId = useId();
  const [search, setSearch] = useState('');
  const { isOpen, toggler } = useModalState();

  // The form is the only source of truth: the convert modal pre-selects SAFEs after this mounts,
  // so a local copy would miss them.
  const selectedSafes: SelectedSafe[] = useWatch({ control, name: 'stepOne.safes' }) ?? [];
  const linkedEventId = useWatch({ control, name: 'stepOne.linkedEvent.id' });
  const selectedIds = selectedSafes.map(({ id }) => id);

  const { safes, fetchNextPage, hasNextPage } = SafeHooks.useInfiniteSafes({
    companyId,
    take,
    search,
  });

  const validSafes =
    linkedEventId === 'no-valuation' ? safes.filter((safe) => safe?.floorValue) : safes;

  const updateSelection = (nextSafes: SelectedSafe[]) => {
    setValue('stepOne.safes', nextSafes);
    setValue(
      'stepOne.safeIds',
      nextSafes.map(({ id }) => id),
    );
  };

  // Appends to the current selection instead of rebuilding it from the visible page, so SAFEs
  // picked before a search stay selected once the search filters them out of the list.
  const handleSelectSafe = (safe: Safe) =>
    updateSelection([...selectedSafes, toSelectedSafe(safe)]);

  const handleDeselectSafe = (id: string) =>
    updateSelection(selectedSafes.filter((safe) => safe.id !== id));

  const describeSafe = ({ stakeholder, investment, issueDate }: SelectedSafe) =>
    `${stakeholder.fullName} · ${currencySign ?? ''}${Number(investment).toLocaleString('en-US')} - ${format(issueDate, 'dd/MM/yyyy')}`;

  return (
    <Popover onOpenChange={toggler} open={isOpen}>
      <PopoverTrigger
        className={twMerge(
          'flex min-h-14 w-full items-center justify-between gap-2 rounded-tl-[4px] rounded-tr-[4px] border-b-[1px] border-gray-700 bg-gray-50 px-3 py-2 text-left text-sm font-[450] text-gray-400',
          isOpen && 'border-brand-700',
        )}
      >
        {selectedSafes.length === 0 ? (
          <span>
            <AppFormattedMessage id="SELECT_SAFE" />
          </span>
        ) : (
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {selectedSafes.slice(0, visibleChipCount).map((safe) => (
              <Tooltip content={describeSafe(safe)} key={safe.id}>
                <div className="flex w-fit items-center gap-3 rounded-3xl bg-brand-50 p-[6px]">
                  <span className="max-w-[220px] truncate text-sm text-gray-700">
                    {safe.stakeholder.fullName}
                  </span>
                  <CrossIcon
                    className="size-[14px] cursor-pointer"
                    // The chip lives inside the trigger button; without this, removing a SAFE
                    // also toggles the popup.
                    onClick={(event: MouseEvent) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleDeselectSafe(safe.id);
                    }}
                  />
                </div>
              </Tooltip>
            ))}
            {selectedSafes.length > visibleChipCount && (
              <span className="text-sm text-gray-700">
                +{selectedSafes.length - visibleChipCount} more
              </span>
            )}
          </div>
        )}
        <ChevronDownIcon
          className={twMerge(
            'shrink-0 transition-transform duration-200 ease-in-out',
            isOpen && 'rotate-180',
          )}
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-[100] flex w-[var(--radix-popover-trigger-width)] flex-col rounded-lg bg-white p-2 font-normal shadow-2xl"
      >
        <div className="flex w-full flex-col gap-3">
          <Input
            icon={<SearchIcon iconColor="#98A2B3" />}
            onChange={setSearch}
            placeholder={<AppFormattedMessage id="SEARCH" />}
            value={search}
            wrapperClassName="w-full"
          />
          {/* The popup is portaled out of the convert sheet, and the sheet's scroll lock cancels
              every wheel and touch scroll outside it at the document, so they must stop here. */}
          <div
            className="flex max-h-[300px] w-full flex-col overflow-y-auto overscroll-contain"
            id={listId}
            onTouchMove={stopScrollLockPropagation}
            onWheel={stopScrollLockPropagation}
          >
            <InfiniteScroll
              className="flex h-fit w-full flex-col items-stretch justify-start gap-1"
              dataLength={validSafes.length}
              hasMore={hasNextPage}
              loader={<Loader />}
              next={fetchNextPage}
              // The list scrolls inside the popup, so pagination must listen to this box, not the page.
              scrollableTarget={listId}
            >
              {validSafes.map((safe) => (
                <FilterItem
                  checked={selectedIds.includes(safe.id)}
                  currencySign={currencySign}
                  id={safe.id}
                  key={safe.id}
                  onChange={(checked) =>
                    checked ? handleSelectSafe(safe) : handleDeselectSafe(safe.id)
                  }
                  safe={safe}
                />
              ))}
            </InfiniteScroll>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
